// client/src/components/game_client/GameSessionContext.jsx
// Контекст игровой сессии.
//
// Итерация 5: витальность (useVitals: health/energy/hunger/thirst).
// Итерация 7: инвентарь — через Redux (не через хук).
//             Источник: useSelector(state => state.game.inventory).
//             Обновляется в useGameClient через dispatch(updateInventory()).

import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import useGameClient from '../../hooks/useGameClient';
import useVitals from '../../hooks/useVitals';
import Gateway from '../../api/gateway';

const GameSessionContext = createContext(null);

// ============================================================================
// EMPTY_INVENTORY — дефолт, если Redux ещё не инициализирован
// ============================================================================
const EMPTY_INVENTORY = {
  items: [],
  capacity: 100,
  used: 0,
  credits: 0,
  weight_used: 0.0,
  volume_used: 0.0,
};

export function GameSessionProvider({ children }) {
  const gameClient = useGameClient();
  const auth = useSelector((state) => state.auth);

  // 🔥 Итерация 7: инвентарь из Redux
  const inventory = useSelector(
    (state) => state.game?.inventory || EMPTY_INVENTORY
  );
  const inventoryLoading = useSelector(
    (state) => state.game?.inventoryLoading ?? true
  );
  const inventoryError = useSelector(
    (state) => state.game?.inventoryError ?? null
  );

  const {
    isConnected,
    isConnecting,
    isInitializing,
    connect,
    disconnect,
    channel,
    characterState,
    selectedCharacterId,
    selectedCharacter,
    loadInventory,
  } = gameClient;

  // 🔥 Итерация 5: витальность через мембрану (WS + HTTP fallback).
  // wsChannel — из connection.channel (в useGameClient возвращается connection).
  const wsChannel = gameClient?.connection?.channel || channel || null;
  const vitalsState = useVitals(wsChannel, selectedCharacterId);

  // Auto-connect при наличии сессии и выбранного персонажа.
  const autoConnectAttemptedRef = useRef(false);

  useEffect(() => {
    if (!auth?.isAuthenticated) return;
    if (!selectedCharacterId) return;
    if (isConnected || isConnecting || isInitializing) return;
    if (autoConnectAttemptedRef.current) return;

    autoConnectAttemptedRef.current = true;

    (async () => {
      try {
        await connect(selectedCharacterId);
      } catch (err) {
        // silent — пользователь увидит ошибку в UI
        // eslint-disable-next-line no-console
        console.warn('[GameSessionContext] auto-connect failed:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.isAuthenticated, selectedCharacterId]);

  // Сброс при отключении.
  useEffect(() => {
    if (!isConnected) {
      autoConnectAttemptedRef.current = false;
    }
  }, [isConnected]);

  // Прокси для Gateway (чтобы компоненты могли импортировать из контекста).
  const api = useCallback(() => Gateway, []);

  // 🔥 Итерация 7: refetchInventory — через loadInventory (WS push + receive)
  const refetchInventory = useCallback(() => {
    if (selectedCharacterId && loadInventory) {
      return loadInventory(selectedCharacterId);
    }
    return Promise.resolve(null);
  }, [selectedCharacterId, loadInventory]);

  const value = {
    ...gameClient,
    auth,
    api,

    // 🔥 Итерация 5: витальность
    vitals: vitalsState.vitals,
    characterMeta: vitalsState.characterMeta,
    vitalsIsLive: vitalsState.isLive,
    vitalsIsStale: vitalsState.isStale,
    refetchVitals: vitalsState.refetch,
    vitalsError: vitalsState.error,
    vitalsLoading: vitalsState.loading,

    // 🔥 Итерация 7: инвентарь — из Redux
    inventory,
    inventoryLoading,
    inventoryError,
    refetchInventory,
  };

  return (
    <GameSessionContext.Provider value={value}>
      {children}
    </GameSessionContext.Provider>
  );
}

export function useGameSession() {
  const ctx = useContext(GameSessionContext);

  if (ctx === null) {
    throw new Error(
      'useGameSession must be used within a <GameSessionProvider>'
    );
  }

  return ctx;
}

export default GameSessionContext;
